<template>
  <Container>
    <section v-if="currentSelectedStyle && !edit" style="display: flex; flex-direction: column; gap: 12px">
      <h3>Estilo vinculado</h3>
      <div class="styles-item-selected">
        <div :class="['styles-item-icon']" :style="`background-color: ${currentSelectedStyle.bg['background-color']};`">
          <v-icon size="15" :color="currentSelectedStyle.title.color">fa-regular fa-palette</v-icon>
        </div>
        {{currentSelectedStyle.label}}
        <div class="d-flex flex justify-end" style="gap: 2px">
          <IconTooltip icon="fa-regular fa-swap" text="Trocar estilo vinculado" @click="handleEdit" clickStyle/>
          <IconTooltip icon="fa-regular fa-eye" text="Ver estilo vinculado" @click="handleOpenStyle(currentSelectedStyle)" clickStyle/>
        </div>
      </div>      

      <Button 
        label="Desvincular estilo à seção" 
        color="bt_pink_bg"
        textColor="bt_pink"
        textSize="0.80rem"
        icon="fa-regular fa-link-slash"
        @click="handleRemoveLayout"
      />

      <div>          
        <CheckBox 
          id="revert" 
          v-model="maintainInheritedStyle" 
          label="Manter estilo mesmo após desvincular" 
          labelFontSize="13px"
          labelFontWeight="600"
        />
      </div>
    </section>

    <section v-if="!currentSelectedStyle || edit" style="display: flex; flex-direction: column; gap: 12px">
      <!-- Mostra Opções de estilo para vincular  -->
      <div v-if="showAvailableStyles || edit">
        <h3 class="pb-3">Estilos disponíveis</h3>
        <div v-for="(item, index) in listAvailableLayouts" :key="item.id">
          <div 
            :class="['styles-item', {'styles-selected': item.id === selectedStyleToBind}]" 
            @click="handleSelectStyle(item.id)"
          >
            <div 
              :class="['styles-item-icon']" 
              :style="`background-color: ${item.bg['background-color']};`"
            >
              <v-icon size="15" :color="item.title.color">fa-regular fa-palette</v-icon>
            </div>
            {{item.label || `Estilo ${index + 1}`}}
          </div>
        </div>

        <div class="d-flex justify-end pt-5" style="gap: 8px">
          <Button
            outlined
            label="Cancelar"
            text-color="bt_color"
            textSize="0.80rem"
            @click="handleShowLayouts"
          />

          <Button 
            :label="edit ? 'Selecionar' : 'Vincular'" 
            :disabled="!selectedStyleToBind" 
            textSize="0.80rem" 
            @click="handleConfirmStyle"
          />          
        </div>
      </div>

      <!-- Sem estilo vinculado  -->
      <Button 
        v-if="!showAvailableStyles && !edit" 
        label="Adicionar estilo à seção" 
        color="bt_color_bg"
        textColor="bt_color"
        textSize="0.80rem"
        icon="fa-regular fa-palette"
        @click="handleShowLayouts"
      />    

      <Button 
        v-if="!hasLayoutDerivedFromThisSection && !showAvailableStyles && !edit" 
        label="Transformar em um estilo" 
        color="bt_green_bg"
        textColor="bt_green"
        textSize="0.80rem"
        icon="fa-regular fa-palette"
        @click="handleTransformInStyle"
      />      
    </section>
    <hr class="_divider"/>

    <ConfirmDialog 
      v-model="dialogConfirmRemove"
      titleMsg="Quero desvincular"
      :showTitleDivider="false" 
      @confirmDone="confirmRemoveLayout"
    >
      <template slot="title">
        <div class="bt_pink_bg confirm-icon">
          <v-icon size="16" color="bt_pink">fa-regular fa-link-slash</v-icon>
        </div>
      </template>

      <template slot="content">
        <span class="confirm-title">
          Ao desvincular o estilo você irá tornar a seção editável novamente.
        </span>
        <span class="confirm-subtitle">
          O estilo matriz não será afetado e você poderá adicioná-lo novamente quando quiser.
        </span>
        <div class="pt-2 pb-5">
          <CheckBox 
            id="dont-show-confirm-again" 
            v-model="dontShowConfirmAgain" 
            label="Não mostrar novamente"
            labelFontSize="14px"
            fixPosition
          />
        </div>
      </template>
    </ConfirmDialog>
  </Container>   
</template>

<script>
import { generateId } from '../../../helpers/generateId'
import { mapGetters } from "vuex"
import { changeNullForStringValue } from '../../../helpers/recursive'
import { createUsingSchema } from '../../../schemas'
import { LAYOUT_SCHEMA } from '../../../schemas/layout/schema'
import Container from '../../_v2/ui/Container.vue'
import Button from '../../_v2/ui/Button.vue'
import ConfirmDialog from '../../_v2/ui/ConfirmDialog.vue'
import IconTooltip from '../../_v2/ui/IconTooltip.vue'
import CheckBox from '../../_v2/ui/Checkbox/Checkbox.vue'

export default {
  name: 'SelectStyle',
  components: {Container, Button, ConfirmDialog, CheckBox, IconTooltip},
  props: {
    selectedSection: {type: Object, required: true},
    types: {type: Object, required: true}
  },

  computed: {
    ...mapGetters(['infos']),
    listAvailableLayouts () {
      return this.infos.layout.filter(item => item.id !== 'default-style').map((item, index) => ({...item, label: item.label || `Estilo ${index + 1}`}))
    },

    currentSelectedStyle () {
      const selected = this.listAvailableLayouts.find(layout => layout.id === this.selectedSection.idLayout)
      return selected
    },

    hasLayoutDerivedFromThisSection () {
      const derivedLayout = this.listAvailableLayouts.find(layout => layout.id.includes(`${this.selectedSection.id}-`))
      if (derivedLayout && derivedLayout.idLayout === this.selectedSection.idLayout) return true
      return false
    }   
  },

  data: () => ({
    showAvailableStyles: false,
    selectedStyleToBind: null,
    dialogConfirmRemove: false,
    dontShowConfirmAgain: false,
    edit: false,
    maintainInheritedStyle: true
  }),

  watch: {
    dontShowConfirmAgain (value) {
      localStorage.setItem('dontShowAgainUnlinkStyleMessage', value)
    }
  },

  methods: {
    handleShowLayouts () {
      this.showAvailableStyles = !this.showAvailableStyles
      if (!this.showAvailableStyles) this.selectedStyleToBind = null 
      this.edit = false
    },

    handleSelectStyle (id) {
      this.selectedStyleToBind = id
    },

    handleConfirmStyle () {
      this.selectedSection.idLayout = this.selectedStyleToBind
      this.edit = false
    },

    handleEdit () {
      this.edit = true
    },

    handleRemoveLayout () {
      const getCustomConfig = localStorage.getItem('dontShowAgainUnlinkStyleMessage')
      if (getCustomConfig) {
        const check = JSON.parse(getCustomConfig)
        if (check) {
          this.confirmRemoveLayout()
          return
        }
      } 

      this.dialogConfirmRemove = true
    },

    confirmRemoveLayout () {
      const currendIdOfSelectedLayout = this.selectedSection.idLayout
      const indexSection = this.infos.sections.findIndex(section => section.id === this.selectedSection.id) 
      if (indexSection !== -1) {
        setTimeout(() => {
          this.infos.sections[indexSection].idLayout = ""
          if (this.maintainInheritedStyle) this.changeStyleOfSectionAfterRemove(currendIdOfSelectedLayout)
          this.maintainInheritedStyle = true
        }, 100)
      } 
      this.showAvailableStyles = false
      this.selectedStyleToBind = null 
    },

    changeStyleOfSectionAfterRemove (idLayout) {
      let previousStyle = this.infos.layout.find(layout => layout.id === idLayout) 
      previousStyle = JSON.parse(JSON.stringify(previousStyle))

      if (this.selectedSection.title) this.selectedSection.title = {...this.selectedSection.title, ...previousStyle.title}
      if (this.selectedSection.subtitle) this.selectedSection.subtitle = {...this.selectedSection.subtitle, ...previousStyle.subtitle}
      if (this.selectedSection.text) this.selectedSection.text = {...this.selectedSection.text, ...previousStyle.text}
      if (this.selectedSection.bg) this.selectedSection.bg = {...this.selectedSection.bg, ...previousStyle.bg}
      if (this.selectedSection.buttons) {
        this.selectedSection.buttons.forEach((btn) => {
          btn['background-color'] = previousStyle.button['background-color']
          btn['color'] = previousStyle.button['color']
          btn['border-radius'] = previousStyle.button['border-radius']
        })
      }
    },

    handleTransformInStyle () {
      console.log('A seção selecionada', this.selectedSection)
      const newId = generateId()
      let newStyle = createUsingSchema(LAYOUT_SCHEMA({
        id: `${this.selectedSection.id}-${newId}`,
        label: `Estilo - ${this.types[this.selectedSection.type]} - ${newId}`,
        title: {
          color: this.selectedSection['title'] ? this.selectedSection['title'].color : "", 
          font: this.selectedSection['title'] ? this.selectedSection['title'].font : "", 
          'font-size': this.selectedSection['title'] ? this.selectedSection['title']['font-size'] : "", 
          weight: this.selectedSection['title'] ? this.selectedSection['title'].weight : "", 
          'font-style': this.selectedSection['title'] ? this.selectedSection['title']['font-style'] : "",
        },
        subtitle: {
          color: this.selectedSection['subtitle'] ? this.selectedSection['subtitle'].color : "", 
          font: this.selectedSection['subtitle'] ? this.selectedSection['subtitle'].font : "", 
          'font-size': this.selectedSection['subtitle'] ? this.selectedSection['subtitle']['font-size'] : "", 
          weight: this.selectedSection['subtitle'] ? this.selectedSection['subtitle'].weight : "", 
          'font-style': this.selectedSection['subtitle'] ? this.selectedSection['subtitle']['font-style'] : "",
        },
        text: {
          color: this.selectedSection['text'] ? this.selectedSection['text'].color : "", 
          font: this.selectedSection['text'] ? this.selectedSection['text'].font : "", 
          'font-size': this.selectedSection['text'] ? this.selectedSection['text']['font-size'] : "", 
          weight: this.selectedSection['text'] ? this.selectedSection['text'].weight : "", 
          'font-style': this.selectedSection['text'] ? this.selectedSection['text']['font-style'] : "",
        },
        observation: {
          color: this.selectedSection['observation'] ? this.selectedSection['observation'].color : "", 
          font: this.selectedSection['observation'] ? this.selectedSection['observation'].font : "", 
          'font-size': this.selectedSection['observation'] ? this.selectedSection['observation']['font-size'] : "", 
          weight: this.selectedSection['observation'] ? this.selectedSection['observation'].weight : "", 
          'font-style': this.selectedSection['observation'] ? this.selectedSection['observation']['font-style'] : "",
        },
        bg: {
          'background-type': this.selectedSection.bg ? this.selectedSection.bg['background-type'] : "",
          'background-color': this.selectedSection.bg ? this.selectedSection.bg['background-color'] : "",
          'background-image': this.selectedSection.bg ? this.selectedSection.bg['background-image'] : "",
          'background-video': this.selectedSection.bg ? this.selectedSection.bg['background-video'] : "",
        },
        button: {
          color: this.selectedSection.buttons.length > 0 ? this.selectedSection.buttons[0].color : "", 
          'background-color': this.selectedSection.buttons.length > 0 ? this.selectedSection.buttons[0]['background-color'] : "", 
          'border-radius': this.selectedSection.buttons.length > 0 ? this.selectedSection.buttons[0]['border-radius'] : "",
        }
      }))
      newStyle = changeNullForStringValue(newStyle)
      console.log('@.@ Estilo criado a partir da sessão --->', newStyle)

      this.infos.layout.push(newStyle)
      this.handleSelectStyle(newStyle.id)
      this.handleConfirmStyle()
    },

    handleOpenStyle (currentStyle) {
      this.$store.dispatch('setOpenStyleFromSection', this.selectedSection.id)
      this.$store.dispatch('setLayoutStyle', {id: currentStyle.id, open: true})
      this.$store.dispatch('setNav', {open: true, sectionId: 'style-config'})
    }
  }
}
</script>

<style scoped>
h3 {
  flex: 1;
  font-size: 16px;
  font-weight: 700;
}

.styles-item-selected, .styles-item {
  display: flex;
  gap: 10px;
  height: 36px;
  align-items: center;
  font-size: 0.9rem;
  transition: all 0.3s;
  border-radius: 4px;
  padding-left: 8px;
}

.styles-item:hover {
  background-color: rgba(102, 109, 128, 0.05);
  cursor: pointer;
}

.styles-item-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background-color: rgba(102, 109, 128, 0.1);
}

.styles-selected {
  background-color: rgba(102, 109, 128, 0.05);
}

.confirm-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  margin-top: 36px;
}

span {
  display: block;
}

.confirm-title {
  font-size: 20px;
  font-weight: 500;
  color: #484D5C;
  padding-top: 4px  
}

.confirm-subtitle {
  font-size: 13.5px;
  font-weight: 400;
  color: #666D80;
  padding: 16px 0 16px 0;  
  letter-spacing: 0.1px;
}
</style>
